import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { map, skip, switchMap, take } from 'rxjs/operators';
import { Api } from '../../../../../../../shared/constants/api';
import { $scan } from '../../../../../../../shared/constants/scan';
import {
  $discoveredScannableDocument,
  $scannableDocumentQuery,
  DiscoveredScannableDocumentStatus,
} from '../../../../../../../shared/constants/scannable-document';
import { IScan } from '../../../../../../../shared/interfaces/scan.interface';
import { IDiscoveredScannableDocument } from '../../../../../../../shared/interfaces/scannable-document.interface';
import { SharedCommonUtility } from '../../../../../../../shared/utils/common.utility';
import { InfoModalComponent } from '../../../../../components/modals/info-modal/info-modal.component';
import { InfoModalOptions } from '../../../../../interfaces/modal-dialog.interface';
import { NotificationPosition } from '../../../../../models/notification.model';
import { BusMessageChannels, BusMessageService } from '../../../../../services/bus-message.service';
import { ModalService } from '../../../../../services/modal.service';
import { NotificationService } from '../../../../../services/notification.service';
import { ScanService } from '../../../../../services/scan.service';
import { ScannableDocumentService } from '../../../../../services/scannable-document.service';
import { TranslateService } from '../../../../../translate/translate.service';
import { DateUtility } from '../../../../../utility/date.utility';
import { ScanOrigin } from '../../../../../../../shared/constants/scanning';
import { AngularUtility } from '../../../../../utility/angular.utility';
import { AclSecurityAdapter } from '../../../../../services/acl.service';
import { UserAclService } from '../../../../../services/user-acl.service';
import { FunctionalArea, SecureAction } from '../../../../../../../shared/constants/security';

@Component({
  selector: 'app-view-scannable-document-urls',
  templateUrl: './view-scannable-document-urls.component.html',
  styleUrls: ['./view-scannable-document-urls.component.scss'],
  standalone: false,
})
export class ViewScannableDocumentUrlsComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription;
  private documentUrl: string;
  private scanId: string;

  private scan$: Observable<IScan>;
  private upload$: Subject<void>;

  public discoveredScannableDocument$: Observable<IDiscoveredScannableDocument>;
  public cardTitle$: Observable<string>;
  public foundDuringText$: Observable<string>;
  public title: string;
  public foundInPages: string[];
  public canUpload: boolean;
  public isMonitoringScan$: BehaviorSubject<boolean>;
  public hasUploadDocumentPermission$: Observable<boolean>;

  constructor(
    private scannableDocumentService: ScannableDocumentService,
    private scanService: ScanService,
    private activatedRoute: ActivatedRoute,
    private busMessageService: BusMessageService,
    private router: Router,
    private translateService: TranslateService,
    private notificationService: NotificationService,
    private modalService: ModalService,
    private userAclService: UserAclService,
  ) {
    this.subscriptions = new Subscription();
    this.upload$ = new Subject<void>();
    this.isMonitoringScan$ = new BehaviorSubject<boolean>(null);
  }

  private getTitle(discoveredScannableDocument: IDiscoveredScannableDocument): string {
    return (
      discoveredScannableDocument[$discoveredScannableDocument.title] ||
      discoveredScannableDocument[$discoveredScannableDocument.url]
    );
  }

  private getData(): void {
    this.scan$ = this.scanService.getScan(this.scanId).pipe(AngularUtility.shareRef());

    this.discoveredScannableDocument$ = this.scan$.pipe(
      map((scan: IScan): IDiscoveredScannableDocument => {
        const findByUrl = (discoveredScannableDocument: IDiscoveredScannableDocument): boolean =>
          discoveredScannableDocument[$discoveredScannableDocument.url] === this.documentUrl;

        const foundDiscoveredScannableDocument: IDiscoveredScannableDocument = scan[$scan.scannableDocuments]?.find(findByUrl);

        if (SharedCommonUtility.isNullish(foundDiscoveredScannableDocument)) {
          this.navigateBackToHomePage();
          return null;
        }

        return foundDiscoveredScannableDocument;
      }),
    );

    this.subscriptions.add(
      this.scan$
        .pipe(map((scan: IScan): boolean => scan[$scan.scanOrigin] === ScanOrigin.monitoring))
        .subscribe(this.isMonitoringScan$),
    );

    this.foundDuringText$ = this.scan$.pipe(
      map((scan: IScan): string => {
        const scanType: string = scan[$scan.scanOrigin] === ScanOrigin.monitoring ? 'monitoring' : 'automated';
        const translationKey: string = `found_during_${scanType}_scan`;
        return this.translateService.instant(translationKey, DateUtility.formatCellDate(new Date(scan[$scan.createdAt]), 'at'));
      }),
    );

    this.cardTitle$ = this.scan$.pipe(
      map((scan: IScan): string => {
        const translationKey: string =
          scan[$scan.scanOrigin] === ScanOrigin.monitoring ? 'pdf_discovered' : 'label_scannable_document_report_found_links';
        return this.translateService.instant(translationKey);
      }),
    );

    this.subscriptions.add(
      this.discoveredScannableDocument$.subscribe((discoveredScannableDocument: IDiscoveredScannableDocument) => {
        const canUpload = (status: DiscoveredScannableDocumentStatus): boolean => {
          return (
            status !== DiscoveredScannableDocumentStatus.submitted_for_testing &&
            status !== DiscoveredScannableDocumentStatus.downloading
          );
        };

        this.title = this.getTitle(discoveredScannableDocument);
        this.foundInPages = discoveredScannableDocument[$discoveredScannableDocument.foundInPages];
        this.canUpload = canUpload(discoveredScannableDocument[$discoveredScannableDocument.status]);
      }),
    );
  }

  private navigateBackToHomePage(): void {
    if (this.isMonitoringScan$.value) {
      this.router.navigate(['/', Api.monitoring, Api.pdf], { skipLocationChange: true });
    } else {
      this.router.navigate(['/', Api.scans], { skipLocationChange: true });
    }
  }

  private navigateBackToResultsPage(): void {
    this.router.navigate(['../..'], {
      relativeTo: this.activatedRoute,
    });
  }

  private subscribeToDigitalPropertyChanges(): void {
    this.subscriptions.add(
      this.busMessageService
        .from(BusMessageChannels.digitalPropertyChange)
        .pipe(skip(1))
        .subscribe(() => {
          this.navigateBackToHomePage();
        }),
    );
  }

  private isInputInvalid(): boolean {
    return SharedCommonUtility.isNullishOrEmpty(this.documentUrl) || SharedCommonUtility.isNullishOrEmpty(this.scanId);
  }

  private async onDownloadDiscoveredScannableDocumentSuccess(): Promise<void> {
    const showInfoModal = async (): Promise<void> => {
      const infoModalOptions: InfoModalOptions = {
        headerKey: 'scannable_document_added_to_inventory',
        bodyHeader: 'scannable_document_added_to_inventory_info',
      };
      const modal: InfoModalComponent = this.modalService.open(InfoModalComponent).instance;
      modal.init(infoModalOptions);
      await modal.container.onClose$.pipe(take(1)).toPromise();
    };

    await showInfoModal();

    this.notificationService.success(
      this.translateService.instant('action_test_this_document_success'),
      NotificationPosition.Toast,
      true,
    );

    this.navigateBackToResultsPage();
  }

  private onDownloadDiscoveredScannableDocumentError(error: any): void {
    console.error('[ViewScannableDocumentUrlsComponent.onDownloadDiscoveredScannableDocumentError]', error);

    this.notificationService.error(this.translateService.instant('action_test_this_document_error'), NotificationPosition.Toast);
  }

  public upload(): void {
    this.upload$.next();
  }

  public ngOnInit(): void {
    this.documentUrl = this.activatedRoute.snapshot.queryParams[$scannableDocumentQuery.documentUrl];
    this.scanId = this.activatedRoute.snapshot.params[Api.scanId];

    this.getData();

    if (this.isInputInvalid()) {
      this.navigateBackToHomePage();
      return;
    }

    this.hasUploadDocumentPermission$ = this.userAclService.createCheckAccessForCurrentUser().pipe(
      map((adapter: AclSecurityAdapter): boolean =>
        adapter
          .useWorkspaceFromUser()
          .useFunctionalActions({ [FunctionalArea.pdf_testing]: [SecureAction.create] })
          .check(),
      ),
    );

    this.subscriptions.add(
      this.upload$
        .pipe(
          switchMap((): Observable<void> => {
            return this.scannableDocumentService.createScannableDocumentFromUrls([this.documentUrl], this.scanId);
          }),
        )
        .subscribe({
          next: this.onDownloadDiscoveredScannableDocumentSuccess.bind(this),
          error: this.onDownloadDiscoveredScannableDocumentError.bind(this),
        }),
    );

    this.subscribeToDigitalPropertyChanges();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
